// Generate a Quote button (GW-470) — wires up a "Get a quote" button on the
// storefront CART page. Clicking it snapshots the current cart into a parts-finder
// quote and takes the shopper to My Quotes (download / convert back to an order).
//
// The button is STATIC HTML the storefront places itself (so it's in the initial
// paint — no injected element, no layout shift). Put the marker attribute on the
// button element and this widget just attaches the click behaviour:
//     <a class="btn btn-checkout" role="button" data-pf-generate-quote data-target="/my-quotes">
//       <i class="fa fa-file-text-o btn-checkout__icon-left" aria-hidden="true"></i>
//       <span class="pf-quote-label">Get a quote</span>
//       <i class="fa fa-arrow-right btn-checkout__icon-right" aria-hidden="true"></i>
//     </a>
// (the busy state swaps the .pf-quote-label text; keep that span).
//
// Cart lines come from OE's own AJAX cart endpoint — GET /ajax/cart/get-cart —
// which the embed fetches same-origin (cookies flow, so it's THIS shopper's cart).
// Its `Items[]` give us { Sku, Description, Qty, Price } where `Price` is the
// VAT-inclusive unit price (matches what we store). No storefront wiring needed.
// Override the endpoint with data-cart-url if it ever differs.
//
// Fallbacks (only used if the ajax cart is unreachable/empty): marker `data-cart`
// JSON, `window.__PF_CART__`, or `[data-pf-cart-line]` elements. Optional client
// info: data-client-name/-company/-email/-phone/-address or window.__PF_QUOTE_CLIENT__
// (else the server defaults the client to the signed-in account).
//
// Destination after generating: the CORE "My Quotes" page (the one hosting the
// <div data-pf-quotes></div> widget). Defaults to /quotes; set the real path with
// data-target="/my-quotes" on the marker or window.__PF_QUOTES_URL__.
import { apiJson } from '../shared/api.js';
import { getTopDocument } from '../shared/dom.js';
import { QUOTES_ENABLED } from '../shared/config.js';

export var id = 'generate-quote-button';

// Module-level in-flight guard. The per-element aria-busy flag isn't enough:
// the storefront re-renders the cart summary (sftarget/sfstate), so a second
// click can land on a FRESH button element with no aria-busy and double-submit
// (→ duplicate quote). This flag lives in the module closure, so it survives any
// DOM re-render. Reset only on a failure path; on success we navigate away.
var _submitting = false;

function readCookie(name) {
  try {
    var doc = getTopDocument();
    var match = (doc.cookie || '')
      .split(';')
      .map(function (c) { return c.trim(); })
      .filter(function (c) { return c.indexOf(name + '=') === 0; })[0];
    return match ? decodeURIComponent(match.slice(name.length + 1)) : '';
  } catch (e) { return ''; }
}

function sessionHeaders() {
  var h = { 'Content-Type': 'application/json' };
  var jwt = readCookie('commerce_features');
  if (jwt) h['X-Commerce-Features'] = jwt;
  var sr = readCookie('Session');
  if (sr) h['X-Session-Reference'] = sr;
  return h;
}

function normalizeLines(arr) {
  if (!Array.isArray(arr)) return [];
  return arr
    .map(function (l) {
      l = l || {};
      return {
        sku: String(l.sku || l.SKU || l.Sku || l.code || '').trim(),
        name: String(l.name || l.title || l.Name || l.Description || '').trim(),
        qty: parseInt(l.qty || l.quantity || l.Qty || 0, 10),
        unitPriceIncl: parseFloat(l.unitPriceIncl || l.priceIncl || l.price || l.Price || l.UnitPriceIncl || 0),
        productId: parseInt(l.productId || l.ProductId || l.productID || 0, 10) || null,
      };
    })
    .filter(function (l) { return l.sku && l.qty > 0; });
}

// OE's ajax cart — the authoritative "current cart" for this shopper. Same-origin
// GET with credentials so the storefront session cookie identifies the cart.
function fetchAjaxCart(marker) {
  var url = (marker.getAttribute('data-cart-url') || '').trim() || (window.location.origin + '/ajax/cart/get-cart');
  return fetch(url, { method: 'GET', credentials: 'same-origin', headers: { Accept: 'application/json' } })
    .then(function (r) { return r.ok ? r.text() : ''; })
    .then(function (txt) {
      if (!txt) return { lines: [], currency: '' };
      var data;
      try { data = JSON.parse(txt); } catch (e) { return { lines: [], currency: '' }; }
      var items = (data && data.Items) || [];
      return { lines: normalizeLines(items), currency: (data && data.Currency) || '' };
    })
    .catch(function () { return { lines: [], currency: '' }; });
}

// Static fallbacks if the ajax cart is unavailable.
function collectStaticLines(marker) {
  var raw = marker.getAttribute('data-cart');
  if (raw) {
    try {
      var lines = normalizeLines(JSON.parse(raw));
      if (lines.length) return lines;
    } catch (e) { /* fall through */ }
  }
  try {
    if (Array.isArray(window.__PF_CART__)) {
      var g = normalizeLines(window.__PF_CART__);
      if (g.length) return g;
    }
  } catch (e) { /* ignore */ }
  var doc = getTopDocument();
  var els = doc.querySelectorAll('[data-pf-cart-line]');
  if (els && els.length) {
    var out = [];
    Array.prototype.forEach.call(els, function (el) {
      out.push({ sku: el.getAttribute('data-sku'), name: el.getAttribute('data-name'), qty: el.getAttribute('data-qty'), unitPriceIncl: el.getAttribute('data-price') });
    });
    return normalizeLines(out);
  }
  return [];
}

// Resolve the cart: ajax cart first, static fallbacks second.
function resolveCart(marker) {
  return fetchAjaxCart(marker).then(function (res) {
    if (res.lines && res.lines.length) return res;
    return { lines: collectStaticLines(marker), currency: '' };
  });
}

function collectClient(marker) {
  var c = {};
  var map = { name: 'data-client-name', company: 'data-client-company', email: 'data-client-email', phone: 'data-client-phone', address: 'data-client-address' };
  Object.keys(map).forEach(function (k) {
    var v = marker.getAttribute(map[k]);
    if (v) c[k] = v;
  });
  try {
    if (window.__PF_QUOTE_CLIENT__ && typeof window.__PF_QUOTE_CLIENT__ === 'object') {
      c = Object.assign({}, window.__PF_QUOTE_CLIENT__, c);
    }
  } catch (e) { /* ignore */ }
  return c;
}

function quotesUrl(marker) {
  var t = (marker.getAttribute('data-target') || '').trim();
  if (t) return t;
  try { if (window.__PF_QUOTES_URL__) return String(window.__PF_QUOTES_URL__); } catch (e) { /* ignore */ }
  return '/my-quotes';
}

function handleQuoteClick(marker) {
  // Guard on the module flag FIRST (survives cart-summary re-renders); the
  // aria-busy check is a cheap secondary for the same-element case.
  if (_submitting || marker.getAttribute('aria-busy') === 'true') return;
  _submitting = true;

  // Busy state swaps the label span (keeping the fa icons) or, if absent, the
  // element's own text.
  var label = marker.querySelector('.pf-quote-label');
  var origText = label ? label.textContent : marker.textContent;
  var setBusy = function (busy, text) {
    marker.style.pointerEvents = busy ? 'none' : '';
    marker.style.opacity = busy ? '0.6' : '';
    marker.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (text != null) {
      if (label) label.textContent = text;
      else marker.textContent = text;
    }
  };
  var release = function () {
    _submitting = false;
    try { setBusy(false, origText); } catch (e) { /* ignore */ }
  };

  setBusy(true, 'Generating…');
  resolveCart(marker).then(function (cart) {
    var lines = cart.lines || [];
    if (!lines.length) {
      release();
      alert('Your cart looks empty. Add some parts before generating a quote.');
      return;
    }
    var body = { items: lines, client: collectClient(marker) };
    if (cart.currency) body.currency = cart.currency;
    apiJson(
      '/api/quotes',
      { method: 'POST', headers: sessionHeaders(), body: JSON.stringify(body) },
      null,
    ).then(function (res) {
      if (res && res.quote) {
        // Success → navigate to My Quotes. Stay "submitting" (we're leaving the page)
        // so a stray click during the redirect can't fire another POST.
        window.location.href = quotesUrl(marker);
      } else {
        release();
        alert('Sorry, we could not generate a quote. Please make sure you are signed in and try again.');
      }
    });
  }).catch(function () {
    // resolveCart/apiJson resolve rather than reject, but guard anyway so a
    // failure can never leave the button permanently stuck.
    release();
  });
}

export function init() {
  var doc = getTopDocument();
  // Quotes killed → don't wire anything.
  if (!QUOTES_ENABLED) { return; }
  // DELEGATION (not per-element binding): the cart summary is rendered/re-rendered
  // by the storefront's own client framework (sftarget/sfstate), so the button can
  // appear — or be replaced — AFTER we run, which would drop a directly-bound
  // listener (the "nothing happens on /checkout/details" bug). A single delegated
  // listener on the document catches the click whenever and however the button is
  // (re)rendered. Guard so we bind it only once.
  if (doc.__pfQuoteDelegated) return;
  doc.__pfQuoteDelegated = true;
  doc.addEventListener(
    'click',
    function (ev) {
      var t = ev.target;
      var marker = t && t.closest ? t.closest('[data-pf-generate-quote]') : null;
      if (!marker) return;
      if (ev.preventDefault) ev.preventDefault();
      handleQuoteClick(marker);
    },
    false,
  );
}
